// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import Axios, { AxiosRequestConfig } from 'axios';
import { injectable } from 'inversify';
import { when } from 'mobx';

import container from '@core/di';
import AuthStore, { TokenRefreshStatus } from '@core/stores/auth';
import { showNotification, NotificationType } from '@shared/components/Notification';

@injectable()
export default class HTTPClient {
  static diToken = Symbol('http-client');
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  
  initialize(defaults: Partial<AxiosRequestConfig>) {
    this.setDefaults(defaults);
    this.setRequestInterceptors();
    this.setResponseInterceptors();
  }

  /// Use bearer authentication.
  private get authHeader(): string {
    return `Bearer ${this.authStore.tokens.access}`;
  }

  /// Set default configs for Axios requests.
  private setDefaults(defaults: Partial<AxiosRequestConfig> = {}) {
    Axios.defaults.baseURL = defaults.baseURL;
    Axios.defaults.headers.common = {
      ...defaults.headers?.common,
      'Content-Type': 'application/json'
    };
  }

  /// Setup interceptors for all Axios requests.
  private setRequestInterceptors() {
    Axios.interceptors.request.use(
      (config) => {
        const { tokens, tokenRefreshStatus } = this.authStore;

        // No changes if there is no access token or if the token is being refreshed.
        // This will probably result in a 401 or 403 error.
        if (!tokens.access || tokenRefreshStatus === TokenRefreshStatus.refreshing) {
          return config;
        }

        // Add the authentication header to the request.
        config.headers.Authorization = this.authHeader;

        return config;
      },
      (error: Error): Error => { throw error; }
    );
  }

  /// Setup interceptors for all Axios responses.
  private setResponseInterceptors() {
    Axios.interceptors.response.use(
      (response) => 
        // On success, return the data from the response.
        ({ ...response, data: response.data.data || response.data }), 
      
        // On error, refresh the token and retry.
        async (error) => {
          const response = error ? error.response : undefined;

          if (!response) {
            console.log("****** Axios responseInterceptors - There is no error response.");
            throw error;          
          }
          
          // if (response.status === 403) {
          //   // this.processForbidden();
          //   console.log("*** response.status 403:", errObj2Str(error));
          // }

          // ReAuth using the refresh token (?).
          if (response.status === 401) {  // Unauthorized or invalid credentials.

            await this.authStore.refreshToken(); // try to refresh the auth.

            await when(() => this.authStore.tokenRefreshStatus === TokenRefreshStatus.refreshed); // Wait for the token to be refreshed.  Note: stuck if refreshToken() fails?

            response.config.headers.Authorization = this.authHeader;

            return Axios(response.config); // Retry the request with the refreshed token.
          }

          // This is probably not needed.  The code will need to handle the 'throw error' below somewhere.
          if (response.status >= 500) {
            showNotification(`http-client - Failed to execute operation. Status#: ${response.status}`, NotificationType.error);
          }

          throw error;
        }
    );
  }

  private processForbidden(msg = '', showMessage = true) {
    if (showMessage) {
      showNotification(msg);
    }
  }
}